import React, { useEffect, useState } from 'react';
import { lt } from 'semver';
import { Step1 } from './Steps/Step1';
import { Step2 } from './Steps/Step2';
import { Step5 } from './Steps/Step5';
import { Step3 } from './Steps/Step3';
import { Step4 } from './Steps/Step4';
import { Step6 } from './Steps/Step6';
import { Step7 } from './Steps/Step7';
import { useQuery } from 'react-query';
import { getAdasImages, getDeviceTelemetry, getMetaDataDevice, getUserInfo } from './service';
import { useIntl } from 'react-intl';
import { CalibrationIntl } from './Calibration.intl';
import { CONTAINER_HEIGHT, CONTAINER_WIDTH, initial, VERSION, DEVICE_STATUSES, Coordinates, UrlParams } from './models';
import { useLocation, useParams } from 'react-router';
import qs from 'qs';

export const steps = [
  {
    id: 1,
    component: <Step1 />,
  },
  {
    id: 2,
    component: <Step2 />,
  },
  {
    id: 3,
    component: <Step3 />,
  },
  {
    id: 4,
    component: <Step4 />,
  },
  {
    id: 5,
    component: <Step5 />,
  },
  {
    id: 6,
    component: <Step6 />,
  },
  {
    id: 7,
    component: <Step7 />,
  },
];

export const QueryKey = {
  adasImages: 'adasImages',
  deviceMetadata: 'deviceMetada',
  userInfo: 'userInfo',
  redLinePosition: 'redLinePosition',
  calibrationDotsPosition: 'calibrationDotsPosition',
  calibrationDotsFormValue: 'calibrationDotsFormValue',
  selectedImage: 'selectedImage',
  imageUrl: 'imageUrl',
  whiteLabel: 'whiteLabel',
  adasImagesRequest: 'adasImagesRequest',
  deviceTelemetry: 'deviceTelemetry',
  token: 'token',
  force: 'force',
  currentMeasurment: 'currentMeasurment',
  prevMeasurment: 'prevMeasurment',
};

const DEFAULT_MEASEURMENT = { value: 'CM', coef: 1 };
export const useToken = () => {
  const location = useLocation();
  const token = qs.parse(location.search, { ignoreQueryPrefix: true }).token;
  const { data } = useQuery(QueryKey.token, () => token);
  return data as string;
};

export const useForce = () => {
  const location = useLocation();
  const force = (qs.parse(location.search, { ignoreQueryPrefix: true }).force ?? 'false') as string;

  const { data } = useQuery(QueryKey.force, () => JSON.parse(force.toLowerCase()));
  return data;
};

export const usePrevMeasurment = () => {
  const { data } = useQuery(QueryKey.prevMeasurment, () => ({ coef: 2.54, value: 'Inches' }));
  return data;
};

export const useCurrentMeasurment = () => {
  const { data } = useQuery(QueryKey.currentMeasurment, () => DEFAULT_MEASEURMENT);
  return data;
};

export const useUserInfo = () => {
  const token = useToken();
  const { data, isLoading, isError } = useQuery(QueryKey.userInfo, () => getUserInfo(token), {
    enabled: !!token,
  });
  return { userInfo: data, isUserLoading: isLoading, hasUserError: isError };
};

export const useDeviceMetadata = (imei: string) => {
  const token = useToken();
  const { orgId } = useParams<UrlParams>();
  const { data, isLoading, isError } = useQuery([QueryKey.deviceMetadata, imei], () => getMetaDataDevice(imei, token, orgId), {
    enabled: !!imei,
  });
  return { deviceMetadata: data, isLoading: isLoading, isError: isError };
};

export const useDeviceTelemetry = (imei: string) => {
  const token = useToken();
  const { data, isLoading, isError } = useQuery(
    [QueryKey.deviceTelemetry, imei],
    () => getDeviceTelemetry(imei, token),
    {
      enabled: !!imei,
    },
  );
  return {
    deviceTelemetry: data,
    isTelemetryLoading: isLoading,
    isTelemetryError: isError,
  };
};

export const useAccessChecker = (imei: string) => {
  const { userInfo } = useUserInfo();
  const { deviceMetadata } = useDeviceMetadata(imei);
  const { deviceTelemetry, isTelemetryError } = useDeviceTelemetry(imei);
  const [response, setResponse] = useState<any>();
  const force = useForce();

  const intl = useIntl();
  useEffect(() => {
    if (userInfo?.entity !== 'user') {
      setResponse({
        type: intl.formatMessage(CalibrationIntl.error),
        message: intl.formatMessage(CalibrationIntl.errorAccessMessage),
        action: {
          title: intl.formatMessage(CalibrationIntl.close),
          method: () => closeAppDialog(),
        },
      });
    } else if (isTelemetryError || (deviceTelemetry?.firmwareVersion && lt(deviceTelemetry.firmwareVersion, VERSION))) {
      setResponse({
        type: intl.formatMessage(CalibrationIntl.error),
        message: intl.formatMessage(CalibrationIntl.errorDeviceMessage),
        action: {
          title: intl.formatMessage(CalibrationIntl.close),
          method: () => closeAppDialog(),
        },
      });
    } else if (!isTelemetryError && deviceTelemetry?.adasCalibrationCompleted) {
      if (!force) {
        setResponse({
          type: intl.formatMessage(CalibrationIntl.error),
          message: intl.formatMessage(CalibrationIntl.errorDeviceCalibratedMessage),
          action: {
            title: intl.formatMessage(CalibrationIntl.close),
            method: () => closeAppDialog(),
          },
        });
      }
    } else {
      setResponse(null);
    }
  }, [userInfo, deviceMetadata, isTelemetryError, deviceTelemetry]);

  return { response, setResponse };
};

export const useRequestAdasImages = (imei: string, max = 9) => {
  const token = useToken();
  const { data, isLoading, isError } = useQuery(
    [QueryKey.adasImagesRequest, imei],
    () => getAdasImages(imei, token, { max }),
    {
      enabled: !!imei && !!token,
    },
  );
  return { isLoadingAdas: isLoading, isErrorAdas: isError };
};

export const useAdasImages = (imei: string) => {
  const token = useToken();
  const { data, isLoading, isError } = useQuery([QueryKey.adasImages, imei], () => getAdasImages(imei, token), {
    enabled: !!imei && !!token,
  });
  return { adasImages: data, isLoadingAdas: isLoading, isErrorAdas: isError };
};

export const useImageUrl = () => {
  const { data } = useQuery(QueryKey.imageUrl, () => ({
    url: undefined,
    id: undefined,
  }));
  return { url: data };
};

export const useSelectedImage = () => {
  const { url } = useImageUrl();
  const [image, setImage] = useState(null);

  useEffect(() => {
    if (url?.url) {
      const newImage = new window.Image(CONTAINER_WIDTH, CONTAINER_HEIGHT);
      newImage.src = (url as { url: string; id: number })?.url;
      setImage(newImage);
    }
  }, [url]);
  return image;
};

export const useRedLinePosition = (position = initial) => {
  const { data } = useQuery(QueryKey.redLinePosition, () => {
    return position;
  });

  return { redLinePosition: data as number };
};

export const useCalibrationDotsPosition = () => {
  const { redLinePosition } = useRedLinePosition();
  const { data } = useQuery(
    QueryKey.calibrationDotsPosition,
    () => {
      const bootomPointY =
        redLinePosition && redLinePosition - 257 >= 0 ? redLinePosition - 257 - (10 + 7 + 4) : 10 + 7 + 4;
      return redLinePosition
        ? [
            {
              xPosition: 14,
              yPosition: redLinePosition,
            },
            {
              xPosition: CONTAINER_WIDTH / 2 - 7,
              yPosition: bootomPointY,
            },
            {
              xPosition: CONTAINER_WIDTH - 14,
              yPosition: redLinePosition,
            },
          ]
        : [];
    },
    { enabled: !!redLinePosition },
  );

  return { calibrationDotsPosition: data as Coordinates[] };
};

export const useImageCoeficient = () => {
  const selectedImage = useSelectedImage();
  const [heightCoef, setHeightCoef] = useState(1);
  const [widthCoef, setWidthCoef] = useState(1);
  useEffect(() => {
    selectedImage?.naturalHeight && setHeightCoef(CONTAINER_HEIGHT / selectedImage.naturalHeight);
    selectedImage?.naturalWidth && setWidthCoef(CONTAINER_WIDTH / selectedImage.naturalWidth);
  }, [selectedImage]);

  return { heightCoef, widthCoef };
};

export const closeAppDialog = () => {
  window.parent.postMessage('close-app-dialog', '*');
};

export const successfullyCalibrated = () => {
  window.parent.postMessage('successfully-calibrated', '*');
}

export const calibrationError = () => {
  window.parent.postMessage('calibration-error', '*');
}
